<script setup>
// Create / edit a library deck by hand. Meta fields + a repeating MCQ block
// (question, 4 options, the correct one, optional difficulty). Saves via
// POST /api/library/decks (create) or PUT /api/library/decks/:id (edit).
// Structure is validated server-side (lib/validateDeck.js); its problems[] are
// surfaced inline. CSV import is a separate, later on-ramp.
import { ref, onMounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";

const route = useRoute();
const router = useRouter();
const editId = route.params.id || null;
const isEdit = computed(() => Boolean(editId));

const LETTERS = ["A", "B", "C", "D"];
const loading = ref(Boolean(editId));
const saving = ref(false);
const error = ref("");
const problems = ref([]);

// `topic` is the deck's display NAME (labelled "Deck name" — distinct from the
// Category's Topic level). `slug` is auto-derived server-side from the name on
// create and preserved on edit; `version` is kept silently — neither is a
// visible field (see bug 507: the top section was confusing duplicate input).
const form = ref({ topic: "", slug: "", version: "", disclaimer: "", tagsText: "", description: "", byline: "" });
const items = ref([newItem()]);

// --- category (taxonomy) picker -------------------------------------------
// Three linked selects (Domain → Subject → Topic) sourced from /api/categories.
// The deepest chosen node is the deck's categoryId; its items inherit it. Empty
// = Uncategorised (the deck then surfaces under "My decks" in the picker).
const categoryTree = ref([]);
const idIndex = ref(new Map()); // id -> { node, parentId }
const selDomain = ref("");
const selSubject = ref("");
const selTopic = ref("");

const categoryId = computed(() => selTopic.value || selSubject.value || selDomain.value || null);
const subjectOpts = computed(() => idIndex.value.get(selDomain.value)?.node.children || []);
const topicOpts = computed(() => idIndex.value.get(selSubject.value)?.node.children || []);

function indexTree(nodes, parentId, map) {
  for (const n of nodes) {
    map.set(n.id, { node: n, parentId });
    if (n.children?.length) indexTree(n.children, n.id, map);
  }
}
async function loadCategories() {
  try {
    const r = await fetch("/api/categories", { credentials: "include" });
    categoryTree.value = r.ok ? (await r.json()).categories || [] : [];
    const m = new Map();
    indexTree(categoryTree.value, null, m);
    idIndex.value = m;
  } catch { /* non-fatal — the picker just shows no options */ }
}
function setCategoryFromId(id) {
  const p = [];
  let cur = id, guard = 0;
  while (cur && guard++ < 5) { p.unshift(cur); cur = idIndex.value.get(cur)?.parentId || null; }
  selDomain.value = p[0] || "";
  selSubject.value = p[1] || "";
  selTopic.value = p[2] || "";
}
function onDomainChange() { selSubject.value = ""; selTopic.value = ""; }
function onSubjectChange() { selTopic.value = ""; }

// Inline category creation — a signed-in tenant starts with an EMPTY taxonomy
// and builds their own. A "➕ New…" option at each level opens a name input; on
// add we POST /api/categories, refetch, and select the new node.
const creating = ref(null); // null | 'domain' | 'subject' | 'topic'
const newName = ref("");
const createBusy = ref(false);
const createError = ref("");

// `area` (the legacy proto-Subject) is no longer a typed field — it's DERIVED
// from the chosen Subject (or Domain) so the column + the distractor context
// stay populated.
const derivedArea = computed(() =>
  idIndex.value.get(selSubject.value)?.node.name ||
  idIndex.value.get(selDomain.value)?.node.name || null
);

// Picking a real value resets the levels below it (and cancels any in-progress
// "add"). Creating a new node is now a separate "+" button, not a dropdown
// option (bug 507) — startCreate opens the inline name field for that level.
function onSelectLevel(level) {
  creating.value = null;
  if (level === "domain") onDomainChange();
  if (level === "subject") onSubjectChange();
}
function startCreate(level) {
  creating.value = level;
  newName.value = "";
  createError.value = "";
}

async function createCategory(level) {
  const name = newName.value.trim();
  if (!name) return;
  const parentId = level === "domain" ? null : level === "subject" ? selDomain.value : selSubject.value;
  createBusy.value = true; createError.value = "";
  try {
    const r = await fetch("/api/categories", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, level, parentId: parentId || undefined })
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.error || "Could not create the category.");
    await loadCategories();
    setCategoryFromId(data.category.id); // selects the new node (and its ancestry)
    creating.value = null; newName.value = "";
  } catch (e) {
    createError.value = e.message;
  } finally {
    createBusy.value = false;
  }
}

function newItem() {
  return { question: "", options: ["", "", "", ""], correctIndex: 0, difficulty: null, type: "", explanation: "", memoryHint: "", citation: "", genBusy: false, genError: "" };
}
function addItem() { items.value.push(newItem()); }
function removeItem(i) { items.value.splice(i, 1); if (items.value.length === 0) items.value.push(newItem()); }

// The only mandatory author input per question is the question text + the
// correct answer (the option marked correct). The three distractors are
// optional to type — left blank, they are generated (here, or automatically on
// save). distractorState classifies an item's non-correct options:
//   "complete" all three filled · "empty" none filled · "partial" some filled.
function distractorState(it) {
  const others = it.options.filter((_, i) => i !== it.correctIndex);
  const filled = others.filter((o) => (o || "").trim()).length;
  if (filled === others.length) return "complete";
  if (filled === 0) return "empty";
  return "partial";
}

// Fetch + fill the three distractors for one item. Throws on failure (the
// caller owns busy/error state). The author supplies question + correct answer;
// Claude proposes the other three, filled in order and left editable.
// POST /api/library/distractors. Off the learner answer loop (see .conventions.md).
async function fillDistractors(it) {
  const question = (it.question || "").trim();
  const correctAnswer = (it.options[it.correctIndex] || "").trim();
  if (!question || !correctAnswer) {
    throw new Error("Enter the question and the correct answer (the option marked correct) first.");
  }
  const r = await fetch("/api/library/distractors", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ question, correctAnswer, area: derivedArea.value || undefined, topic: form.value.topic || undefined })
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || "We couldn't generate distractors just now — please try again.");
  const fill = [...(data.distractors || [])];
  for (let i = 0; i < it.options.length; i++) {
    if (i === it.correctIndex) continue;
    const next = fill.shift();
    if (typeof next === "string") it.options[i] = next;
  }
}

// "Generate distractors" button handler — manages the per-item spinner + error.
async function generateDistractors(it) {
  it.genError = "";
  it.genBusy = true;
  try {
    await fillDistractors(it);
  } catch (e) {
    it.genError = e.message;
  } finally {
    it.genBusy = false;
  }
}

async function load() {
  try {
    const r = await fetch(`/api/library/decks/${editId}`, { credentials: "include" });
    if (!r.ok) throw new Error("We couldn't load this deck just now.");
    const d = (await r.json()).deck;
    form.value = {
      topic: d.topic, slug: d.slug, version: d.version || "",
      disclaimer: d.disclaimer || "", tagsText: (d.tags || []).join(", "),
      description: d.description || "", byline: d.byline || ""
    };
    setCategoryFromId(d.categoryId || null);
    items.value = d.items.length
      ? d.items.map((it) => ({
          question: it.question,
          options: [0, 1, 2, 3].map((i) => it.options[i] ?? ""),
          correctIndex: it.correctIndex,
          difficulty: it.difficulty ?? null,
          type: it.type || "",
          explanation: it.explanation || "",
          memoryHint: it.memoryHint || "",
          citation: it.citation || "",
          genBusy: false,
          genError: ""
        }))
      : [newItem()];
  } catch (e) {
    error.value = e.message;
  } finally {
    loading.value = false;
  }
}

async function save() {
  error.value = ""; problems.value = []; saving.value = true;
  try {
    // Minimum input is question + correct answer. Distractors left blank are
    // generated now; partially-filled options aren't clobbered — the author is
    // asked to finish or clear them.
    for (let idx = 0; idx < items.value.length; idx++) {
      const it = items.value[idx];
      it.genError = "";
      if (!(it.question || "").trim()) throw new Error(`Question ${idx + 1} needs question text.`);
      if (!(it.options[it.correctIndex] || "").trim()) throw new Error(`Question ${idx + 1}: enter the correct answer (the option marked correct).`);
      const state = distractorState(it);
      if (state === "partial") {
        it.genError = "Fill the remaining options, or clear them all to auto-generate.";
        throw new Error(`Question ${idx + 1}: finish the options or clear them to auto-generate.`);
      }
      if (state === "empty") {
        it.genBusy = true;
        try {
          await fillDistractors(it);
        } catch (e) {
          it.genError = e.message;
          throw new Error(`Question ${idx + 1}: ${e.message}`);
        } finally {
          it.genBusy = false;
        }
      }
    }

    const body = {
      topic: form.value.topic,
      // slug auto-derives server-side when omitted (create); preserved on edit.
      slug: form.value.slug || undefined,
      version: form.value.version || undefined,
      disclaimer: form.value.disclaimer || undefined,
      description: form.value.description || undefined,
      byline: form.value.byline || undefined,
      area: derivedArea.value || undefined, // derived from the chosen Subject/Domain
      tags: form.value.tagsText.split(",").map((t) => t.trim()).filter(Boolean),
      categoryId: categoryId.value || null,
      items: items.value.map((it) => ({
        question: it.question.trim(),
        options: it.options.map((o) => o.trim()),
        correctIndex: it.correctIndex,
        difficulty: it.difficulty ? Number(it.difficulty) : undefined,
        type: it.type || undefined,
        explanation: (it.explanation || "").trim() || undefined,
        memoryHint: (it.memoryHint || "").trim() || undefined,
        citation: (it.citation || "").trim() || undefined
      }))
    };
    const url = isEdit.value ? `/api/library/decks/${editId}` : "/api/library/decks";
    const r = await fetch(url, {
      method: isEdit.value ? "PUT" : "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) {
      if (Array.isArray(data.problems)) problems.value = data.problems;
      throw new Error(data.error || "We couldn't save the deck just now — please try again.");
    }
    router.push(`/library/${data.deck.id}`);
  } catch (e) {
    error.value = e.message;
  } finally {
    saving.value = false;
  }
}

onMounted(async () => {
  await loadCategories();
  if (editId) await load();
});
</script>

<template>
  <div class="container-fluid p-0">
    <router-link to="/library" class="btn btn-sm btn-link px-0 mb-2"><i class="bi bi-arrow-left me-1"></i>My Library</router-link>
    <h1 class="h3 mb-3">{{ isEdit ? "Edit deck" : "New deck" }}</h1>

    <div v-if="loading" class="text-muted py-4"><span class="spinner-border spinner-border-sm me-2"></span>Loading…</div>

    <form v-else @submit.prevent="save">
      <div v-if="error" class="alert alert-warning py-2">
        {{ error }}
        <ul v-if="problems.length" class="mb-0 mt-1 small">
          <li v-for="(p, i) in problems" :key="i">{{ p }}</li>
        </ul>
      </div>

      <!-- Meta -->
      <div class="card border-0 shadow-sm mb-3">
        <div class="card-body">
          <div class="row g-3">
            <div class="col-12">
              <label class="form-label small text-muted">Deck name *</label>
              <input v-model="form.topic" type="text" class="form-control" placeholder="e.g. Atrial Fibrillation" required>
            </div>

            <!-- Category: Domain → Subject → Topic. Each level is labelled, and
                 the + button beside it adds your own node inline (bug 507). -->
            <div class="col-12">
              <label class="form-label small text-muted">Category <span class="text-muted">— where it appears in Topics</span></label>
              <div class="row g-2">
                <div class="col-12 col-md-4">
                  <label class="form-label small text-muted mb-1">Domain</label>
                  <div class="input-group input-group-sm">
                    <select v-model="selDomain" @change="onSelectLevel('domain')" class="form-select">
                      <option value="">Choose…</option>
                      <option v-for="d in categoryTree" :key="d.id" :value="d.id">{{ d.name }}</option>
                    </select>
                    <button type="button" class="btn btn-outline-secondary" title="Add a domain" @click="startCreate('domain')"><i class="bi bi-plus-lg"></i></button>
                  </div>
                </div>
                <div class="col-12 col-md-4">
                  <label class="form-label small text-muted mb-1">Subject</label>
                  <div class="input-group input-group-sm">
                    <select v-model="selSubject" @change="onSelectLevel('subject')" class="form-select" :disabled="!selDomain">
                      <option value="">Choose…</option>
                      <option v-for="s in subjectOpts" :key="s.id" :value="s.id">{{ s.name }}</option>
                    </select>
                    <button type="button" class="btn btn-outline-secondary" title="Add a subject" :disabled="!selDomain" @click="startCreate('subject')"><i class="bi bi-plus-lg"></i></button>
                  </div>
                </div>
                <div class="col-12 col-md-4">
                  <label class="form-label small text-muted mb-1">Topic</label>
                  <div class="input-group input-group-sm">
                    <select v-model="selTopic" @change="onSelectLevel('topic')" class="form-select" :disabled="!selSubject">
                      <option value="">Choose…</option>
                      <option v-for="t in topicOpts" :key="t.id" :value="t.id">{{ t.name }}</option>
                    </select>
                    <button type="button" class="btn btn-outline-secondary" title="Add a topic" :disabled="!selSubject" @click="startCreate('topic')"><i class="bi bi-plus-lg"></i></button>
                  </div>
                </div>
              </div>

              <!-- Inline create: type a name for the level whose + you clicked -->
              <div v-if="creating" class="d-flex flex-wrap gap-2 align-items-center mt-2 p-2 bg-light rounded">
                <span class="small text-muted text-capitalize">New {{ creating }}:</span>
                <input
                  v-model="newName"
                  type="text"
                  class="form-control form-control-sm"
                  style="max-width: 240px"
                  :placeholder="`${creating} name`"
                  autofocus
                  @keyup.enter.prevent="createCategory(creating)"
                >
                <button type="button" class="btn btn-sm btn-primary" :disabled="createBusy || !newName.trim()" @click="createCategory(creating)">
                  <span v-if="createBusy" class="spinner-border spinner-border-sm me-1"></span>Add
                </button>
                <button type="button" class="btn btn-sm btn-link text-decoration-none" @click="creating = null">Cancel</button>
              </div>
              <div v-if="createError" class="text-danger small mt-1">{{ createError }}</div>

              <div class="form-text">Pick as deep as you can, or use <i class="bi bi-plus-lg"></i> to add your own. Leave blank to keep it under “My decks”.</div>
            </div>

            <div class="col-12">
              <label class="form-label small text-muted">Tags <span class="text-muted">— comma-separated, optional</span></label>
              <input v-model="form.tagsText" type="text" class="form-control" placeholder="exam, year-1">
            </div>
            <div class="col-12 col-md-8">
              <label class="form-label small text-muted">Description <span class="text-muted">— a short line shown on the cover + in the library, optional</span></label>
              <input v-model="form.description" type="text" class="form-control" placeholder="e.g. Core concepts every beginner should know.">
            </div>
            <div class="col-12 col-md-4">
              <label class="form-label small text-muted">Byline <span class="text-muted">— attribution, optional</span></label>
              <input v-model="form.byline" type="text" class="form-control" placeholder="e.g. Meat &amp; Livestock Australia">
            </div>
            <div class="col-12">
              <label class="form-label small text-muted">Disclaimer <span class="text-muted">— optional</span></label>
              <input v-model="form.disclaimer" type="text" class="form-control" placeholder="Illustrative content — not professionally validated.">
            </div>
          </div>
        </div>
      </div>

      <!-- Items -->
      <div v-for="(it, idx) in items" :key="idx" class="card border-0 shadow-sm mb-2">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <span class="fw-semibold text-muted small">Question {{ idx + 1 }}</span>
            <button type="button" class="btn btn-sm btn-link text-danger p-0" @click="removeItem(idx)"><i class="bi bi-x-lg"></i></button>
          </div>
          <textarea v-model="it.question" class="form-control mb-2" rows="2" placeholder="Question text" required></textarea>
          <div v-for="(o, oi) in it.options" :key="oi" class="input-group input-group-sm mb-1">
            <span class="input-group-text">
              <input class="form-check-input mt-0" type="radio" :name="`correct-${idx}`" :value="oi" v-model="it.correctIndex" :title="`Mark ${LETTERS[oi]} correct`">
              <span class="ms-2">{{ LETTERS[oi] }}</span>
            </span>
            <!-- Only the option marked correct is required. The other three may be
                 typed, or left blank to be generated (button below / on save). -->
            <input
              v-model="it.options[oi]"
              type="text"
              class="form-control"
              :placeholder="oi === it.correctIndex ? `Option ${LETTERS[oi]} — correct answer (required)` : `Option ${LETTERS[oi]} (optional — auto-generated if left blank)`"
              :required="oi === it.correctIndex"
            >
          </div>

          <!-- Distractor generation: the minimum is question + correct answer.
               The three wrong options are optional — generate them here, or leave
               blank and they're generated on save. -->
          <div class="d-flex align-items-center gap-2 mb-1">
            <button
              type="button"
              class="btn btn-sm btn-outline-primary"
              :disabled="it.genBusy"
              @click="generateDistractors(it)"
              title="Generate the three wrong options from the question and the correct answer"
            >
              <span v-if="it.genBusy" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-stars me-1"></i>Generate distractors
            </button>
            <span class="text-muted small">Optional — fills the three non-correct options. Left blank, they're generated when you save.</span>
          </div>
          <div v-if="it.genError" class="text-danger small mb-1">{{ it.genError }}</div>

          <textarea v-model="it.explanation" class="form-control form-control-sm mt-2" rows="2" placeholder="Explanation — shown after answering (optional)"></textarea>
          <div class="row g-2 mt-1">
            <div class="col-12 col-md-6">
              <input v-model="it.memoryHint" type="text" class="form-control form-control-sm" placeholder="Memory hint / mnemonic (optional)">
            </div>
            <div class="col-12 col-md-6">
              <input v-model="it.citation" type="text" class="form-control form-control-sm" placeholder="Citation / reference (optional)">
            </div>
          </div>
          <div class="row g-2 mt-1">
            <div class="col-6 col-md-3">
              <input v-model="it.difficulty" type="number" min="1" max="5" class="form-control form-control-sm" placeholder="Difficulty 1–5">
            </div>
            <div class="col-6 col-md-3">
              <input v-model="it.type" type="text" class="form-control form-control-sm" placeholder="Type (optional)">
            </div>
          </div>
        </div>
      </div>

      <button type="button" class="btn btn-sm btn-outline-secondary mb-3" @click="addItem"><i class="bi bi-plus-lg me-1"></i>Add question</button>

      <div class="d-flex gap-2">
        <button type="submit" class="btn btn-primary" :disabled="saving">
          <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
          {{ isEdit ? "Save changes" : "Create deck" }}
        </button>
        <router-link to="/library" class="btn btn-outline-secondary">Cancel</router-link>
      </div>
      <p class="text-muted small mt-2">Only the question and its correct answer are required per question — leave the other options blank to have them generated for you. After saving, open the deck to review the content and generate its audio.</p>
    </form>
  </div>
</template>
